<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <!-- Total Users Card -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between">
            <div>
              <p
                class="text-sm font-medium text-gray-500 uppercase tracking-wider"
              >
                Total Users
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2">
                {{ formatNumber(totalUsers) }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-indigo-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
          </div>
          <!-- <div class="mt-4 flex items-center text-sm">
            <span class="text-green-600 font-medium">+12%</span>
            <span class="text-gray-500 ml-2">vs last month</span>
          </div> -->
        </div>

        <!-- Active Users Card -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between">
            <div>
              <p
                class="text-sm font-medium text-gray-500 uppercase tracking-wider"
              >
                Active
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2">
                {{ formatNumber(activeUsers) }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <!-- <div class="mt-4 flex items-center text-sm">
            <span class="text-gray-500"
              >{{ ((activeUsers / totalUsers) * 100).toFixed(1) }}% of
              total</span
            >
          </div> -->
        </div>

        <!-- Inactive Users Card -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between">
            <div>
              <p
                class="text-sm font-medium text-gray-500 uppercase tracking-wider"
              >
                Inactive
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2">
                {{ formatNumber(inactiveUsers) }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Pending Approval Card -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between">
            <div>
              <p
                class="text-sm font-medium text-gray-500 uppercase tracking-wider"
              >
                Pending
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2">
                {{ formatNumber(pendingUsers) }}
              </p>
            </div>
            <div
              class="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Bar -->
      <div
        class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6"
      >
        <div class="flex flex-col lg:flex-row gap-4">
          <!-- Search -->
          <div class="flex-1 relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              v-model="filters.search"
              type="text"
              placeholder="Search users..."
              class="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
              @input="debouncedSearch"
            />
          </div>

          <!-- Role Filter -->
          <div class="w-full lg:w-48">
            <select
              v-model="filters.role"
              class="block w-full pl-3 pr-10 py-2 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm rounded-lg"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="verification_officer">Verification Officer</option>
              <option value="supervisor">Supervisor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <!-- Status Filter -->
          <div class="w-full lg:w-48">
            <select
              v-model="filters.status"
              class="block w-full pl-3 pr-10 py-2 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm rounded-lg"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <!-- Reset Button -->
          <button
            @click="resetFilters"
            class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            RESET FILTERS
          </button>

          <!-- Add User Button -->
          <button
            @click="openUserForm"
            class="px-4 py-2 bg-indigo-800 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            ADD USER
          </button>
        </div>
      </div>

      <!-- Users Table -->
      <div
        class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <!-- <div class="px-6 py-4 border-b border-gray-100">
          <h2 class="text-lg font-semibold text-gray-900">Users</h2>
          <p class="text-sm text-gray-500 mt-1">
            Showing {{ filteredUsers.length }} of {{ totalUsers }} users
          </p>
        </div> -->

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  User
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Role
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Last Active
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Joined
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="filteredUsers.length === 0">
                <td colspan="7" class="px-6 py-12 text-center">
                  <div class="flex flex-col items-center">
                    <svg
                      class="w-12 h-12 text-gray-300 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                    <p class="text-gray-500 text-lg mb-1">No users found</p>
                    <p class="text-gray-400 text-sm">
                      Try adjusting your search or filters
                    </p>
                  </div>
                </td>
              </tr>
              <tr
                v-for="user in paginatedUsers"
                :key="user.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <img
                        :src="
                          user.avatar ||
                          `https://ui-avatars.com/api/?name=${user?.name?.replace(
                            ' ',
                            '+'
                          )}&background=3b82f6&color=fff&bold=true`
                        "
                        :alt="user.name"
                        class="h-10 w-10 rounded-lg object-cover"
                      />
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ user.name }}
                      </div>
                      <div class="text-sm text-gray-500">{{ user.email }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="roleClasses[user.role]"
                  >
                    {{ formatRole(user.role) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <StatusBadge :status="user.status" />
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                    <div class="text-sm font-medium text-gray-900">
                      {{ user.last_active_date }}
                    </div>
                    <div class="text-sm text-gray-500">
                      {{ user.last_active_time }}
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                    <div class="text-sm font-medium text-gray-900">
                      {{ user.date }}
                    </div>
                    <div class="text-sm text-gray-500">{{ user.time }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex items-center space-x-2">
                    <button
                      @click="editUser(user)"
                      class="text-indigo-800 hover:text-indigo-700 p-1 rounded hover:bg-blue-50"
                      title="Edit user"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <!-- <button
                      @click="toggleUserStatus(user)"
                      class="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                      :title="
                        user.status === 'Active'
                          ? 'Deactivate user'
                          : 'Activate user'
                      "
                    >
                      <svg
                        v-if="user.status === 'active'"
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button> -->
                    <button
                      @click="confirmDelete(user)"
                      class="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Delete user"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          class="px-6 py-4 border-t border-gray-100 flex items-center justify-between"
        >
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-500">Items per page:</span>
            <select
              v-model="itemsPerPage"
              class="border border-gray-200 rounded-lg text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>

          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              {{ paginationStart }}-{{ paginationEnd }} of
              {{ filteredUsers.length }}
            </span>
            <div class="flex items-center space-x-2">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  class="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Form Modal -->
    <div
      v-if="showUserForm"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          @click="closeUserForm"
        ></div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen"
          >&#8203;</span
        >

        <div
          class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3
              class="text-lg font-semibold text-gray-900 mb-4"
              id="modal-title"
            >
              {{ editingUser ? "Edit User" : "Add New User" }}
            </h3>

            <form @submit.prevent="saveUser" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Full Name</label
                >
                <input
                  v-model="userForm.name"
                  type="text"
                  required
                  class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Email</label
                >
                <input
                  v-model="userForm.email"
                  type="email"
                  required
                  class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
              </div>

              <div class="w-full flex gap-2">
                <div class="w-1/2">
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Gender</label
                  >
                  <select
                    v-model="userForm.gender"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div class="w-1/2">
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Role</label
                  >
                  <select
                    v-model="userForm.role"
                    required
                    class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select role</option>
                    <option value="Verification Officer">
                      Verification Officer
                    </option>
                    <option value="Supervisor">Supervisor</option>
                    <option value="SMS">SMS</option>
                  </select>
                </div>
              </div>

              <div v-if="!editingUser">
                <div class="w-full flex gap-2">
                  <div class="w-1/2">
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Password</label
                    >
                    <input
                      v-model="userForm.password"
                      type="password"
                      :required="!editingUser"
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter password"
                    />
                  </div>
                  <div class="w-1/2">
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Confirm Password</label
                    >
                    <input
                      v-model="userForm.password_confirmation"
                      type="password"
                      :required="!editingUser"
                      class="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter password again"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  @click="closeUserForm"
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {{ editingUser ? "Update" : "Create" }} User
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <!-- ... delete modal content ... -->
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          @click="closeDeleteModal"
        ></div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen"
          >&#8203;</span
        >

        <div
          class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="bg-white px-6 pt-6 pb-4">
            <div class="flex items-center justify-center mb-4">
              <div
                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100"
              >
                <svg
                  class="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <h3 class="text-lg font-medium text-gray-900 text-center mb-2">
              Delete User
            </h3>
            <p class="text-sm text-gray-500 text-center mb-6">
              Are you sure you want to delete {{ userToDelete?.name }}? This
              action cannot be undone.
            </p>
            <div class="flex justify-center gap-3">
              <button
                @click="closeDeleteModal"
                class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                @click="deleteUser"
                class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Action Button -->
    <button
      @click="getUsers"
      class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-800 text-white shadow-lg transition-all duration-300 hover:bg-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label="Refresh page"
    >
      <!-- Refresh icon (SVG) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="{ 'animate-spin': isRefreshing }"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>
  </div>

  <div
    v-if="isRefreshing"
    class="fixed inset-0 ml-64 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
  >
    <div class="relative">
      <!-- Outer ring -->
      <div class="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
      <!-- Inner spinning gradient ring -->
      <div
        class="w-16 h-16 border-4 border-transparent rounded-full absolute top-0 left-0 border-t-indigo-500 border-r-indigo-700 animate-spin"
      ></div>
      <!-- Center dot -->
      <div
        class="w-3 h-3 bg-indigo-800 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import api from "@/utils/api";

import { useNotifications } from "@/composables/useNotifications";

// Notification system
const { showSuccess, showError, formatValidationErrors } = useNotifications();

const users = ref([]);
const isRefreshing = ref(false);
const showDeleteModal = ref(false);
const userToDelete = ref(null);

// Role classes for badges
const roleClasses = {
  admin: "bg-purple-100 text-purple-800",
  verification_officer: "bg-indigo-100 text-indigo-800",
  supervisor: "bg-green-100 text-green-800",
  viewer: "bg-gray-100 text-gray-800",
};

// Stats
const totalUsers = computed(() => users.value.length);
const activeUsers = computed(
  () => users.value.filter((u) => u.status === "Active").length
);
const inactiveUsers = computed(
  () => users.value.filter((u) => u.status === "Inactive").length
);
const pendingUsers = computed(
  () => users.value.filter((u) => u.status === "Pending").length
);

// Filters
const filters = ref({
  search: "",
  role: "",
  status: "",
});

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(10);

// Modal state
const showUserForm = ref(false);
const editingUser = ref(null);
const userForm = ref({
  name: "",
  email: "",
  gender: "",
  role: "",
  password: "",
});

// Filtered users
const filteredUsers = computed(() => {
  return users.value.filter((user) => {
    const matchesSearch =
      !filters.value.search ||
      user.name.toLowerCase().includes(filters.value.search.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.value.search.toLowerCase());

    const matchesRole = !filters.value.role || user.role === filters.value.role;
    const matchesStatus =
      !filters.value.status || user.status === filters.value.status;

    return matchesSearch && matchesRole && matchesStatus;
  });
});

// Paginated users
const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredUsers.value.slice(start, end);
});

// Pagination helpers
const totalPages = computed(() =>
  Math.ceil(filteredUsers.value.length / itemsPerPage.value)
);
const paginationStart = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);
const paginationEnd = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, filteredUsers.value.length)
);

// Debounced search
let searchTimeout;
const debouncedSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
  }, 300);
};

// Reset filters
const resetFilters = () => {
  filters.value = {
    search: "",
    role: "",
    status: "",
  };
  currentPage.value = 1;
};

// Format helpers
const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

const formatRole = (role) => {
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Modal functions
const openUserForm = () => {
  editingUser.value = null;
  userForm.value = {
    name: "",
    email: "",
    gender: "",
    role: "",
    password: "",
  };
  showUserForm.value = true;
};

const getUsers = async () => {
  isRefreshing.value = true;
  try {
    const response = await api.get("/api/v1/users");
    users.value = response.data.data;
  } catch (error) {
    console.log(error);
  } finally {
    isRefreshing.value = false;
  }
};

const editUser = (user) => {
  editingUser.value = user;
  userForm.value = {
    name: user.name,
    email: user.email,
    role: user.role,
    gender: user.gender,
    password: "",
  };
  showUserForm.value = true;
};

const closeUserForm = () => {
  showUserForm.value = false;
};

const saveUser = async () => {
  // if (!userForm.value.validate()) {
  //   showWarning(
  //     "Validation Error",
  //     "Please correct the errors in the form",
  //     [],
  //     5000
  //   );
  // }
  if (editingUser.value) {
    // Update existing user
    Object.assign(editingUser.value, userForm.value);
  } else {
    // Add new user
    try {
      const response = await api.post("/api/v1/users", userForm.value);
      console.log(response);
      showSuccess("Success", "User saved successfully", null, 3000);
      closeUserForm();
      getUsers();
    } catch (error) {
      console.log(error);
      const details = formatValidationErrors(error);
      showError("Error", "Failed to save user", details, 5000);
    }
  }
};

// const toggleUserStatus = (user) => {
//   user.status = user.status === "Active" ? "Inactive" : "Active";
// };

const confirmDelete = (client) => {
  userToDelete.value = client;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  userToDelete.value = null;
};

const deleteUser = () => {
  closeDeleteModal();
};

// Watch for filter changes to reset page
watch(
  [
    () => filters.value.search,
    () => filters.value.role,
    () => filters.value.status,
  ],
  () => {
    currentPage.value = 1;
  }
);

// Initialize
onMounted(() => {
  getUsers();
});
</script>
